import React, { useEffect, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  Heading,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { AiFillEye } from "react-icons/ai";
import ExportToSlimModal from "./ExportToSlimModal";
import { formatDate, formatSamplingFor } from "../../utils";
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MAPBOX_ACCESS_TOKEN } from "../../services";
import { FaMapMarkerAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { COC_STATUS } from "../../constants";

export default function ViewCOCModal({
  data,
  userRole,
  isCollection = false,
  onApproveCollection,
  onUpdateStatus,
  prepareOrderExcelData,
  prepareSampleExcelData,
  refetchData,
  onMarkAsSeen,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const handleOpen = () => {
    onOpen();
    // Mark as seen when modal opens
    if (onMarkAsSeen) {
      let isStatusValid = false;
      let isUnseen = false;

      if (userRole === "admin") {
        // Admin should mark as seen for PENDING or RECEIVED status
        isStatusValid = [COC_STATUS.PENDING, COC_STATUS.RECEIVED].includes(data.status);
        isUnseen = data.seenByAdmin !== undefined && data.seenByAdmin === false;
      } else {
        // Customer should mark as seen for ANALYSED or DA_COMPLETED status
        isStatusValid = [COC_STATUS.ANALYSED, COC_STATUS.DA_COMPLETED].includes(data.status);
        isUnseen = data.seenByCustomer !== undefined && data.seenByCustomer === false;
      }
      
      if (isStatusValid && isUnseen) {
        onMarkAsSeen(data._id);
      }
    }
  };

  const testTypes = [
    { label: "Validation Analysis Order", value: "validation-analysis-order" },
    {
      label: "Laboratory Composite Order",
      value: "laboratory-composite-order",
    },
    { label: "Detailed Analysis Order", value: "detailed-analysis-order" },
  ];
  var blankCount = 0;

  const questions = [
    "1. Security Seal Number",
    "2. Sampling Address",
    "3. Sampling Date",
    "4. Number of Samples",
    "5. Sampling Locations",
    "6. Are there any discrepancies and/or visible damage/leakage? If Yes - Please record them in the Certificate of Analysis Comments section.",
  ];

  // const formatDate = (value, monthForm) => {
  //   value = value.split("/").reverse().join("-");
  //   value = new Date(value);
  //   return (
  //     value.getDate() +
  //     "-" +
  //     value.toLocaleString("en-us", { month: monthForm }) +
  //     "-" +
  //     value.getFullYear()
  //   );
  // };

  const reloadData = async () => {
    await refetchData();
    onClose();
  };

  const onApproveClick = async () => {
    await onApproveCollection(data._id);
    onClose();
  };

  const onUpdateStatusClick = async () => {
    await onUpdateStatus(data, "pending");
    onClose();
  };

  return (
    <>
      <Tooltip
        label={isCollection ? "View Data Collection" : "View Digital COC Form"}
      >
        <IconButton
          bgColor="primary.100"
          color="primary.main"
          fontSize="xl"
          icon={<AiFillEye />}
          isRound
          onClick={handleOpen}
        />
      </Tooltip>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isCollection ? "Data Collection" : "Digital Chain Of Custody Form"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box>
              <SimpleGrid columns={2} spacing={3} mb="3">
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Company Name
                  </Heading>
                  <Text>
                    {data.clientName !== "" ? data.clientName : "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Contact Name
                  </Heading>
                  <Text>{data.contactName}</Text>
                </Box>
              </SimpleGrid>
              <SimpleGrid columns={2} spacing={3} mb="3">
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Client Email
                  </Heading>
                  <Text>{data.email}</Text>
                </Box>
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Phone
                  </Heading>
                  <Text>{data.phone !== "" ? data.phone : "N/A"}</Text>
                </Box>
              </SimpleGrid>
            </Box>
            <Divider borderColor="primary.main" my="3" />
            <Box>
              <SimpleGrid columns={2} spacing={3} mb="3">
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Date Sampled
                  </Heading>
                  <Text>{formatDate(data.dateSampled)}</Text>
                </Box>
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Order Type
                  </Heading>
                  <Text>
                    {testTypes.find((opt) => opt.value === data.samplingFor)
                      ?.label ??
                      (data.samplingFor
                        ? formatSamplingFor(data.samplingFor)
                        : "N/A")}
                  </Text>
                </Box>
              </SimpleGrid>
              <SimpleGrid columns={2} spacing={3} mb="3">
                <Box>
                  <Heading as="h6" fontSize="sm">
                    {data.propertyType !== "vehicle"
                      ? "Property Address"
                      : "Vehicle Details"}
                  </Heading>
                  <Text>
                    {data.propertyType !== "vehicle"
                      ? data.address +
                        ", " +
                        data.city +
                        ", " +
                        data.state +
                        ", " +
                        data.postcode
                      : data.VIN + ", " + data.rego + ", " + data.regoState}
                  </Text>
                </Box>
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Sampling Technique
                  </Heading>
                  <Text>
                    {data.samplingTechnique !== ""
                      ? data.samplingTechnique
                      : "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Heading as="h6" fontSize="sm">
                    Security Seal No.
                  </Heading>
                  <Text>
                    {data.securitySealNo !== "" ? data.securitySealNo : "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Heading as="h6" fontSize="sm" textTransform="capitalize">
                    Testing Location
                  </Heading>
                  <Text textTransform="capitalize">
                    {data.propertyType ? data.propertyType : "N/A"}
                  </Text>
                </Box>
                {/* {data.identifier && (
                  <Box>
                    <Heading as="h6" fontSize="sm">
                      Lab Sample Identifier
                    </Heading>
                    <Text>
                      {formatDate(data.dateCreated, "long").replaceAll(
                        "-",
                        ""
                      ) +
                        "-" +
                        data.identifierName +
                        "-" +
                        data.identifier}
                    </Text>
                  </Box>
                )} */}
              </SimpleGrid>
            </Box>
            {!isCollection && (
              <>
                <Divider borderColor="primary.main" my="3" />
                <Heading as="h6" fontSize="sm" mb="2">
                  Sample Reception Checklist
                </Heading>
                {data.questions?.length > 0 ? (
                  <Box>
                    <Text mb={3}>
                      Please verify that the following information on the sample
                      tubes and the security-sealed bag matches the information
                      stated on the Digital Chain of Custody Form:
                    </Text>
                    {questions.map((item, i) => {
                      return (
                        <Box key={i} mb={3}>
                          <Heading as={"h6"} fontSize={"sm"}>
                            {item}
                          </Heading>
                          <Text ms={3} mb={1}>
                            {data.questions[i]?.answer ? "Yes" : "No"}
                          </Text>
                          {i < 5
                            ? data.questions[i]?.answer !== null &&
                              !data.questions[i]?.answer && (
                                <>
                                  <Heading
                                    as={"h6"}
                                    fontSize={"sm"}
                                    ms={3}
                                    mb={1}
                                  >
                                    Comment
                                  </Heading>
                                  <Text
                                    p={2}
                                    border={"1px"}
                                    borderColor={"gray.200"}
                                    rounded={"lg"}
                                    ms={3}
                                  >
                                    {data.questions[i]?.comment}
                                  </Text>
                                </>
                              )
                            : data.questions[i]?.answer !== null &&
                              data.questions[i]?.answer && (
                                <>
                                  <Heading
                                    as={"h6"}
                                    fontSize={"sm"}
                                    ms={3}
                                    mb={1}
                                  >
                                    Comment
                                  </Heading>
                                  <Text
                                    p={2}
                                    border={"1px"}
                                    borderColor={"gray.200"}
                                    rounded={"lg"}
                                    ms={3}
                                  >
                                    {data.questions[i]?.comment}
                                  </Text>
                                </>
                              )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Text textAlign={"center"} color={"gray"}>
                    Not Received Yet
                  </Text>
                )}
              </>
            )}
            {isCollection && (
              <>
                <Divider borderColor="primary.main" my="3" />
                <Heading as="h6" fontSize="sm" mb="3">
                  Location
                </Heading>
                <Map
                  mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
                  initialViewState={{
                    longitude: data.location.longitude,
                    latitude: data.location.latitude,
                    zoom: 14,
                  }}
                  style={{
                    width: "100%",
                    height: "200px",
                  }}
                  mapStyle="mapbox://styles/mapbox/streets-v9"
                >
                  <Marker
                    longitude={data.location.longitude}
                    latitude={data.location.latitude}
                    anchor="bottom"
                  >
                    <FaMapMarkerAlt fontSize="24px" color="red" />
                  </Marker>
                </Map>
              </>
            )}
            <Divider borderColor="primary.main" my="3" />
            <Heading as="h6" fontSize="sm" mb="3">
              Samples
            </Heading>
            <TableContainer>
              <Table variant="striped" borderWidth="1px" mb="2">
                <Thead>
                  <Tr>
                    {!isCollection && (
                      <Th textAlign="center">Client Reference Number</Th>
                    )}
                    <Th textAlign="center">Sampling Location</Th>
                    {isCollection && <Th textAlign="center">Video</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {data.samples.map((item, i) => {
                    if ((i + 1) % 20 === 1) {
                      blankCount++;
                      return (
                        <>
                          <Tr>
                            {!isCollection && (
                              <Td py="2" textAlign="center">
                                {"FB" + blankCount}
                              </Td>
                            )}
                            <Td py="2" textAlign="center">
                              Field Blank
                            </Td>
                            {isCollection && (
                              <Td py="2" textAlign="center">
                                N/A
                              </Td>
                            )}
                          </Tr>
                          <Tr>
                            {!isCollection && (
                              <Td py="2" textAlign="center">
                                {i + 1}
                              </Td>
                            )}
                            <Td py="2" textAlign="center">
                              {item.value}
                            </Td>
                            {isCollection && (
                              <Td py="2" textAlign="center">
                                {item.videoUrl ? (
                                  <Link
                                    href={item.videoUrl}
                                    color="primary.main"
                                    textDecoration="underline"
                                    target="_blank"
                                  >
                                    Video Link
                                  </Link>
                                ) : (
                                  "N/A"
                                )}
                              </Td>
                            )}
                          </Tr>
                        </>
                      );
                    } else {
                      return (
                        <Tr>
                          {!isCollection && (
                            <Td py="2" textAlign="center">
                              {i + 1}
                            </Td>
                          )}
                          <Td py="2" textAlign="center">
                            {item.value}
                          </Td>
                          {isCollection && (
                            <Td py="2" textAlign="center">
                              {item.videoUrl ? (
                                <Link
                                  href={item.videoUrl}
                                  color="primary.main"
                                  textDecoration="underline"
                                  target="_blank"
                                >
                                  Video Link
                                </Link>
                              ) : (
                                "N/A"
                              )}
                            </Td>
                          )}
                        </Tr>
                      );
                    }
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
          {/* Modal Footer */}
          {data.status === "received" && userRole === "admin" && (
            <ModalFooter>
              {!data.slimsOrderId && (
                <ExportToSlimModal data={data} refetchData={reloadData} />
              )}
            </ModalFooter>
          )}
          {isCollection && data.status === "pending" && (
            <ModalFooter>
              <Button
                bgColor="black"
                borderRadius="lg"
                fontWeight="medium"
                py="6"
                onClick={onApproveClick}
              >
                Approve
              </Button>
            </ModalFooter>
          )}
          {data.status === "draft" && userRole !== "admin" && (
            <ModalFooter>
              <Button
                bgColor="black"
                borderRadius="lg"
                fontWeight="medium"
                py="6"
                me="3"
                onClick={() => navigate(`/edit-order/${data._id}`)}
              >
                Edit COC
              </Button>
              <Button
                bgColor="black"
                borderRadius="lg"
                fontWeight="medium"
                py="6"
                onClick={onUpdateStatusClick}
              >
                Update Status
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
